<template>
  <div class="admin-card h-100">
    <div class="card-body p-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0 font-weight-bold" style="color:#0f172a;">
          <i class="bi bi-activity mr-1"></i> Recent Activity
        </h6>
        <div class="d-flex align-items-center">
          <small class="text-secondary mr-2">
            <span v-if="lastRefreshed">Updated {{ lastRefreshedFromNow }}</span>
          </small>
          <button class="btn btn-light border" @click="fetchActivity" :disabled="loading" title="Refresh">
            <i class="bi bi-arrow-clockwise" :class="{ 'anim-spin': loading }"></i>
          </button>
          <button class="btn btn-light border ml-1" @click="clearFeed" title="Clear">
            <i class="bi bi-x"></i>
          </button>
        </div>
      </div>

      <div v-if="loading && events.length === 0" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-secondary"></div>
      </div>

      <div v-else-if="events.length === 0" class="text-center text-secondary py-4">
        <i class="bi bi-inbox" style="font-size:1.5rem;"></i>
        <p class="mt-2 mb-0"><small>No activity recorded yet.</small></p>
        <small>Activity is logged when admin actions are taken.</small>
      </div>

      <div v-else style="max-height:300px;overflow-y:auto;">
        <div
          v-for="(event, index) in events"
          :key="index"
          class="d-flex align-items-start py-2 border-bottom"
        >
          <div
            class="rounded-circle d-flex align-items-center justify-content-center mr-2 flex-shrink-0"
            :class="eventBgClass(event.type)"
            style="width:28px;height:28px;"
          >
            <i :class="['bi', eventIcon(event.type)]" style="font-size:.7rem;color:#fff;"></i>
          </div>
          <div class="flex-grow-1" style="min-width:0;">
            <p class="mb-0" style="font-size:.82rem;">
              <strong>{{ eventLabel(event.type) }}</strong>
              <span v-if="event.payload && event.payload.company_name" class="text-secondary">
                — {{ event.payload.company_name }}
              </span>
            </p>
            <small class="text-secondary">{{ event.actor }} · {{ fromNow(event.timestamp) }}</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const POLL_INTERVAL = 30000; // 30 seconds

export default {
  name: 'AdminActivityFeed',

  data() {
    return {
      loading: false,
      events: [],
      lastRefreshed: null,
      pollTimer: null,
    };
  },

  computed: {
    lastRefreshedFromNow() {
      return this.lastRefreshed ? moment(this.lastRefreshed).fromNow() : '';
    },
  },

  mounted() {
    this.fetchActivity();
    this.pollTimer = setInterval(this.fetchActivity, POLL_INTERVAL);
  },

  beforeDestroy() {
    clearInterval(this.pollTimer);
  },

  methods: {
    async fetchActivity() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/admin/activity');
        this.events        = data;
        this.lastRefreshed = new Date();
      } catch (e) {
        // silent — stale data is fine
      } finally {
        this.loading = false;
      }
    },

    clearFeed() {
      this.events = [];
    },

    fromNow(timestamp) {
      return moment(timestamp).fromNow();
    },

    eventLabel(type) {
      const labels = {
        'company.suspended':   'Company Suspended',
        'company.registered':  'New Sign-up',
        'admin.impersonated':  'Impersonation',
        'inventory.deleted':   'Inventory Deleted',
        'billing.changed':     'Billing Changed',
        'plan.changed':        'Plan Changed',
      };
      return labels[type] || type;
    },

    eventIcon(type) {
      const icons = {
        'company.suspended':   'bi-slash-circle',
        'company.registered':  'bi-person-plus',
        'admin.impersonated':  'bi-person-badge',
        'inventory.deleted':   'bi-trash',
        'billing.changed':     'bi-calendar-event',
        'plan.changed':        'bi-arrow-repeat',
      };
      return icons[type] || 'bi-circle';
    },

    eventBgClass(type) {
      const classes = {
        'company.suspended':   'bg-danger',
        'company.registered':  'bg-primary',
        'admin.impersonated':  'bg-primary',
        'inventory.deleted':   'bg-danger',
        'billing.changed':     'bg-dark',
        'plan.changed':        'bg-dark',
      };
      return classes[type] || 'bg-secondary';
    },
  },
};
</script>

<style scoped>
.admin-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
.anim-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
</style>
